import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar } from "lucide-react";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);

  return (
    <main data-testid="blog-list-page">
      <section className="mds-container pt-14 pb-6">
        <span className="mds-badge">Blog & Tips</span>
        <h1 className="font-heading font-semibold text-4xl sm:text-5xl mt-3 tracking-tight leading-tight">
          Practical guides. No fluff.
        </h1>
        <p className="text-[#4A5D54] mt-4 max-w-2xl leading-relaxed text-lg">
          Deep-dives, comparisons and hands-on tips from someone who runs this stack every single day.
        </p>
      </section>

      <section className="mds-container pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {posts.length === 0 ? (
          <p className="text-[#7A8C84]">Loading…</p>
        ) : (
          posts.map((p, i) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className="mds-card group overflow-hidden p-0"
              data-testid={`blog-card-${p.slug}`}
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div className="mds-fade-up">
                {p.cover_image && (
                  <div className="h-44 overflow-hidden">
                    <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-[#7A8C84] mb-3">
                    <Calendar size={12} />
                    <span>{new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-xl leading-snug group-hover:text-[#6B8F73] transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-[#4A5D54] text-sm mt-2 leading-relaxed line-clamp-3">{p.excerpt}</p>
                </div>
              </div>
            </Link>
          ))
        )}
      </section>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <main className="mds-container py-24 text-center">
      <h1 className="font-heading text-3xl">Post not found</h1>
      <Link to="/blog" className="mds-btn mds-btn-primary mt-4">Back to blog</Link>
    </main>
  );
  if (!post) return <main className="mds-container py-24"><p>Loading…</p></main>;

  return (
    <main data-testid={`blog-post-${post.slug}`}>
      <article className="mds-container pt-10 pb-16 max-w-3xl mx-auto">
        <Link to="/blog" className="mds-link inline-flex items-center gap-1 mb-8"><ArrowLeft size={16} /> Blog</Link>
        {post.cover_image && (
          <div className="rounded-2xl overflow-hidden mb-8 border border-[#E5E3DB]">
            <img src={post.cover_image} alt="" className="w-full h-72 object-cover" />
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-[#7A8C84] mb-3">
          <Calendar size={14} />
          <span>{new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
        <h1 className="font-heading font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.1] mb-6">{post.title}</h1>
        <p className="text-lg text-[#4A5D54] leading-relaxed mb-10">{post.excerpt}</p>
        <div className="mds-prose whitespace-pre-line text-base">{post.content}</div>
      </article>
    </main>
  );
}
